"use client";

import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { HubConnection } from "@microsoft/signalr";
import { SignalRContext } from "./SignalRContext";
import { createHubConnection } from "./connection";
import { useAuthStore } from "@/lib/auth/auth-store";

export function SignalRProvider({ children }: { children: ReactNode }) {
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const sessionStatus = useAuthStore((s) => s.sessionState.status);

  useEffect(() => {
    if (sessionStatus !== "authenticated") {
      setConnection((prev) => {
        if (prev) void prev.stop();
        return null;
      });
      setIsConnected(false);
      return;
    }

    const conn = createHubConnection();

    conn.onreconnected(() => setIsConnected(true));
    conn.onreconnecting(() => setIsConnected(false));
    conn.onclose(() => setIsConnected(false));

    conn.start().then(
      () => setIsConnected(true),
      (err: unknown) => {
        console.warn("[SignalR] Failed to connect:", err);
      },
    );
    setConnection(conn);

    return () => {
      void conn.stop();
      setConnection(null);
      setIsConnected(false);
    };
  }, [sessionStatus]);

  const value = useMemo(
    () => ({ connection, isConnected }),
    [connection, isConnected],
  );

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
}
